/* ==========================================================================
   sable-overrides.js — Sable Mae customisations on top of the Bricks theme
   Loaded from layout/theme.liquid. Documented in
   stores/sable-mae/theme/code-changes.md — update that ledger when you edit
   this file. Remove the <script> tag in theme.liquid to disable all of it.

   Four behaviours. The first three are on the collection product card, the
   fourth on the product page:
     1. Tapping a colour swatch changes the variant IN PLACE instead of
        navigating to the product page. The variant's photo replaces slide 0 of
        the existing carousel, so the dots still count the product's real media
        and the shopper can still swipe through the other photos.
     2. The card's single button is "Quick view" and it always opens the quick
        view modal, for every product, carrying the swatch the shopper picked.
     3. The collection's filter button reads "Filter & Sort" wherever the
        separate sort selector is not on screen, because the drawer it opens
        carries sorting too.
     4. The product page's price carries a "You save $X" / "Compare at" caption
        pair, kept in step with the selected variant, and the variant-picker
        labels lose their trailing colon so CSS can set them as the reference
        does. See the `PDP price stack` block at the foot of this file.

   Why this is needed: Bricks ships in-card variant preview but gates it behind
   `!isMobile()` (minified as `!_()`, where `_ = () => o.is(a.Mobile)`), so on
   touch the swatch is only an <a href> to the variant's product page. And
   blocks/_add-to-cart-button.liquid branches on
   `{% if closest.product.has_only_default_variant %}` — single-variant products
   get a real cart form while everything else gets
   `on:click="#QuickViewSection/open/…"`. That split is exactly what made one
   button do two different things, so the override takes the button over and
   sends every product down the quick-view path.

   The modal is opened by calling `open(url, event)` on Bricks' own
   `<quick-view-section id="QuickViewSection">` custom element — the same entry
   point its declarative `#QuickViewSection/open/<url>` binding uses. Passing the
   event lets Bricks put its own loading state on the button while it fetches.
   ========================================================================== */
(function () {
  'use strict';

  var SWATCH = '.swatches-v2__swatch';
  var CARD = 'product-card-v2';
  var productCache = {};  // handle -> Promise

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function sized(url, w) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // Slot 0 of the media carousel is "the variant photo": selecting a swatch
  // rewrites that one image and leaves slides 1..N (the product's other photos)
  // alone, so the dots still count the real media and swiping still works.
  function setFirstSlideImage(card, url) {
    var gallery = card.querySelector('.product-media-container__gallery');
    if (!gallery) return;
    var item = gallery.querySelector('.smart-list__item');
    var img = item && item.querySelector('img');
    if (!img) return;

    // Remember the stock slide-0 photo once, so the default variant restores it.
    if (img.dataset.sableOrigSrc === undefined) {
      img.dataset.sableOrigSrc = img.getAttribute('src') || '';
      img.dataset.sableOrigSrcset = img.getAttribute('srcset') || '';
    }
    if (url) {
      var next = sized(url, 800);
      // Preload before swapping, otherwise the slot goes blank for a frame or
      // more while the new photo decodes — visible on every tap.
      // srcset must be cleared too, or the browser keeps serving the old
      // candidate and the src change appears to do nothing.
      var swap = function () {
        img.setAttribute('srcset', '');
        img.setAttribute('src', next);
      };
      var pre = new Image();
      pre.onload = swap;
      pre.onerror = swap;
      pre.src = next;
    } else {
      img.setAttribute('src', img.dataset.sableOrigSrc);
      img.setAttribute('srcset', img.dataset.sableOrigSrcset);
    }
    var wrapper = img.closest('.image');
    if (wrapper) wrapper.classList.add('loaded');
  }

  function applyVariantImage(card, variantId) {
    var handle = handleFor(card);
    if (!handle) return;
    getProduct(handle).then(function (p) {
      if (!p) return;
      var variant = null;
      for (var i = 0; i < p.variants.length; i++) {
        if (String(p.variants[i].id) === String(variantId)) { variant = p.variants[i]; break; }
      }
      var url = variant && variant.featured_image && variant.featured_image.src;
      setFirstSlideImage(card, url || null);
    });
  }

  function selectSwatch(card, sw) {
    var siblings = sw.parentNode.querySelectorAll(SWATCH);
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('swatches-v2--active');
    }
    sw.classList.add('swatches-v2--active');
    if (sw.dataset.variantId) {
      card.dataset.sableVariantId = sw.dataset.variantId;
      applyVariantImage(card, sw.dataset.variantId);
    }

    // Return to slot 0 so the newly chosen variant is what is on screen, even if
    // the shopper had swiped away. Selecting again while already on slot 0 just
    // changes the photo and stays put.
    var gallery = card.querySelector('.product-media-container__gallery');
    if (gallery && typeof gallery.setActiveIndex === 'function') {
      try { gallery.setActiveIndex(0); } catch (e) { /* carousel not ready */ }
    }
  }

  function handleFor(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function getProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(root() + 'products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  // Bricks' own quick-view entry point. Its declarative
  // `on:click="#QuickViewSection/open/<url>"` binding resolves to exactly this
  // call, so driving it by hand behaves identically — including the loading
  // state it puts on whatever element the event came from.
  function openQuickView(card, e) {
    var section = document.getElementById('QuickViewSection');
    if (!section || typeof section.open !== 'function') return false;

    var handle = handleFor(card);
    if (!handle) return false;

    // Carry the swatch the shopper picked into the modal, so the quick view
    // opens on the variant the card is showing rather than the default one.
    // The modal is the product page's own markup, which honours ?variant=.
    var url = root() + 'products/' + handle;
    var chosen = card.dataset.sableVariantId;
    if (chosen) url += '?variant=' + encodeURIComponent(chosen);

    section.open(url, e);
    return true;
  }

  // Capture on `window` so this runs BEFORE Bricks' own document-level capture
  // delegation, which is what lets stopPropagation() suppress the quick view.
  window.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    // --- 1. swatch selects a variant in place -----------------------------
    var sw = target.closest(SWATCH);
    if (sw) {
      var swCard = sw.closest(CARD);
      if (swCard) {
        e.preventDefault();
        e.stopPropagation();
        selectSwatch(swCard, sw);
      }
      return;
    }

    // --- 2. the card's one button always opens the quick view ---------------
    // Stock Bricks gives single-variant products a real add-to-cart form and
    // everything else a quick-view opener, so the same button did two different
    // things. Take it over for both, so "Quick view" always means quick view.
    var buy = target.closest('.product-card-v2__add-to-cart');
    if (!buy) return;
    var card = buy.closest(CARD);
    if (!card) return;

    // Suppress Bricks' own handler — the form submit on single-variant
    // products, and the duplicate open on everything else.
    e.preventDefault();
    e.stopPropagation();
    openQuickView(card, e);
  }, true);

  // --- 3. the filter button says what it actually opens ---------------------
  // Bricks labels it "Filters" at every width, but the drawer it opens also
  // carries the sort control (`facet-filters.liquid:74` renders a Sort by
  // selector at the top of the drawer body). On desktop there is a separate
  // sort selector in the bar, so "Filters" is right there; on mobile that
  // selector is not rendered and the button is the only way to reach sorting.
  // So the label follows the sort selector: if it is not on screen, this button
  // is doing both jobs and should say so.
  function labelFilterButton() {
    var btn = document.querySelector('.collection-section__filter-button');
    if (!btn) return;
    var sort = document.querySelector('.collection-section__header .collection-section__sort-selector');
    var sortVisible = !!(sort && sort.getBoundingClientRect().width > 0);
    var text = sortVisible ? 'Filters' : 'Filter & Sort';
    var label = btn.querySelector('.button-v2__label') || btn;
    if (label.textContent.trim() !== text) label.textContent = text;
    btn.setAttribute('aria-label', text);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelFilterButton);
  } else {
    labelFilterButton();
  }
  window.addEventListener('resize', labelFilterButton);
  document.addEventListener('collection:change', function () { setTimeout(labelFilterButton, 300); });

  // Warm the product JSON for every card so a swatch tap can find the variant's
  // photo without waiting on a request.
  function warm() {
    document.querySelectorAll(CARD).forEach(function (card) {
      var h = handleFor(card);
      if (h) getProduct(h);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', warm);
  } else {
    warm();
  }
  document.addEventListener('collection:change', function () { setTimeout(warm, 300); });

  // ---- PDP price stack: "You save $X" / "Compare at" ---------------------
  // Bricks renders only the two money figures. The reference labels each one
  // instead of striking the compare-at through, so the captions are appended
  // here and CSS §10 places all four cells on a grid. The saving is derived
  // from the two figures on screen rather than from variant JSON, so it can
  // never disagree with what the shopper is reading.

  var PDP = '.group.product-details';

  // Read a displayed money string as a number. Handles "$1,234.56" and the
  // decimal-comma locales ("1.234,56") the theme can emit on other markets.
  function moneyValue(text) {
    var m = String(text).match(/\d[\d.,]*/);
    if (!m) return NaN;
    var t = m[0];
    if (/,\d{1,2}$/.test(t)) t = t.replace(/\./g, '').replace(',', '.');
    else t = t.replace(/,/g, '');
    return parseFloat(t);
  }

  // Render `n` in the same shape as `sample` — same currency symbol, position
  // and separators — so the caption matches the price above it in any market.
  // The sample is a textContent read straight off the theme's markup, so it
  // arrives padded with the source's newlines; collapse them or the caption
  // carries them into the DOM.
  function moneyLike(sample, n) {
    var s = String(sample).replace(/\s+/g, ' ').trim();
    var m = s.match(/\d[\d.,]*/);
    if (!m) return n.toFixed(2);
    var decComma = /,\d{1,2}$/.test(m[0]);
    var parts = n.toFixed(2).split('.');
    var body = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, decComma ? '.' : ',') +
      (decComma ? ',' : '.') + parts[1];
    return s.slice(0, m.index) + body + s.slice(m.index + m[0].length);
  }

  function note(box, kind, text) {
    var el = box.querySelector('.sable-price-note--' + kind);
    if (!el) {
      el = document.createElement('span');
      el.className = 'sable-price-note sable-price-note--' + kind;
      box.appendChild(el);
    }
    if (el.textContent !== text) el.textContent = text;
  }

  function priceNotes(host) {
    var box = host.querySelector('.price__container');
    if (!box) return;
    var sale = box.querySelector('.price__sale-price') ||
      box.querySelector('.price__regular-price');
    var compare = box.querySelector('.price__compare-at-price');
    var saving = sale && compare
      ? moneyValue(compare.textContent) - moneyValue(sale.textContent)
      : NaN;
    // Not on sale, or the two figures do not produce a real saving: carry no
    // captions at all rather than a "You save $0.00".
    if (!(saving > 0)) {
      box.querySelectorAll('.sable-price-note').forEach(function (el) { el.remove(); });
      return;
    }
    note(box, 'save', 'You save ' + moneyLike(sale.textContent, saving));
    note(box, 'trad', 'Compare at');
  }

  // "Color:" -> "Color", so CSS §10 can uppercase and track it the way the
  // reference sets METAL / SIZE. The case is CSS so the DOM keeps real words.
  function optionLabels(host) {
    host.querySelectorAll('.variant-picker__option-label').forEach(function (el) {
      var t = el.textContent.replace(/\s*:\s*$/, '');
      if (t !== el.textContent) el.textContent = t;
    });
  }

  // THERE IS MORE THAN ONE BUY BOX PER DOCUMENT (ledger C208). The product page
  // renders `.group.product-details` once; the quick-view modal renders a second
  // one, on a collection page that has no product page at all, and it is built
  // AFTER load and rebuilt on every product whose card is opened. So this pass
  // is per-host, not per-document: one observer per host, kept in a WeakSet so a
  // host is never bound twice, and a document-level watch that picks up a host
  // that did not exist at DOMContentLoaded.
  //
  // Bricks re-renders the price and the picker on every variant change, with no
  // event to hook, so watch the buy box. Our own writes are made with that
  // host's observer disconnected — otherwise appending a caption schedules
  // another pass. setTimeout rather than requestAnimationFrame: rAF does not
  // fire in a background/hidden tab, which is exactly where the agent browser
  // verifies.
  // ---- The cost band follows the variant ---------------------------------
  // blocks/sable-cost-band.liquid derives every ledger row and both bars from
  // the SELLING PRICE, in cents, at render time. Bricks re-renders the price
  // and the picker on a variant change but not our block, so the band kept
  // whatever price the page first loaded with — on `maren-polished-steel-set`
  // it printed $41.00 / $82.00 + / Our cost $20.08 while the buy box read
  // $33.00. Four of 85 products are affected.
  //
  // It is recomputed here rather than re-fetched: the whole block is five
  // multiplications, so there is nothing to ask the server for, and reading the
  // price off the screen is the same rule §10's captions already follow — the
  // band can never disagree with the figure above it.
  //
  // LIQUID'S `divided_by` TRUNCATES ON TWO INTEGERS. Mirror that with
  // Math.floor or the JS and the server render disagree by a cent, and the
  // first paint visibly corrects itself. Percentages are owner rulings
  // (cost-model.md §6) — if they change in the block, change them here in the
  // same edit or the band lies the moment a shopper picks a second variant.
  function costRows(price) {
    var piece = Math.floor(price * 2877 / 10000);
    var freight = Math.floor(price * 1233 / 10000);
    var packaging = Math.floor(freight * 20 / 100);
    var tax = Math.floor((piece + freight) * 45 / 1000);
    var processing = Math.floor(price * 29 / 1000) + 30;
    return {
      rows: [piece, freight, packaging, tax, processing],
      our: piece + freight + packaging + tax + processing,
      retail: price * 2
    };
  }

  // The product page renders the band in its own full-width section
  // (sections/sable-product-band.liquid, C205/C206) OUTSIDE the buy box, which
  // is the reason it never saw a variant change in the first place. The quick
  // view keeps it inside the column, so the in-host band wins there — a modal
  // opened over a product page updates its own band and leaves the page's alone.
  function bandFor(host) {
    var inside = host.querySelector('.sable-cost');
    if (inside) return inside;
    if (host.classList.contains('quick-view__blocks')) return null;
    return document.querySelector('.sable-band .sable-cost');
  }

  function setBar(bar, priceText, fillPct, markup, cost) {
    var p = bar.querySelector('.sable-cost__bar-price');
    if (p && p.textContent.trim() !== priceText) p.textContent = priceText;
    var fill = bar.querySelector('.sable-cost__fill');
    if (fill) fill.style.height = fillPct + '%';
    var m = bar.querySelector('.sable-cost__markup');
    if (m) m.style.flexGrow = markup;
    var c = bar.querySelector('.sable-cost__cost');
    if (c) c.style.flexGrow = cost;
  }

  function costBand(host) {
    var band = bandFor(host);
    if (!band) return;

    var box = host.querySelector('.price__container');
    var shown = box && (box.querySelector('.price__sale-price') ||
      box.querySelector('.price__regular-price'));
    if (!shown) return;
    var dollars = moneyValue(shown.textContent);
    if (!(dollars > 0)) return;

    var price = Math.round(dollars * 100);
    var r = costRows(price);
    var sample = shown.textContent;
    var money = function (cents) { return moneyLike(sample, cents / 100); };

    // Shape checks, not defensive noise: writing six values into a five-row
    // ledger would put Our cost on the Card processing line and read as a
    // price, not as a bug. If the block is ever restructured this does nothing
    // and the server render stands.
    var dds = band.querySelectorAll('.sable-cost__row dd');
    if (dds.length !== 6) return;
    var vals = r.rows.concat([r.our]);
    for (var i = 0; i < 6; i++) {
      var t = money(vals[i]);
      if (dds[i].textContent !== t) dds[i].textContent = t;
    }

    var bars = band.querySelectorAll('.sable-cost__bar');
    if (bars.length !== 2) return;
    setBar(bars[0], money(price), Math.floor(price * 100 / r.retail),
      price - r.our, r.our);
    setBar(bars[1], money(r.retail) + ' +', 100, r.retail - r.our, r.our);

    var chart = band.querySelector('.sable-cost__chart');
    if (chart) {
      chart.setAttribute('aria-label', 'Price comparison: Sable Mae ' +
        money(price) + ' against traditional retail of at least ' +
        money(r.retail) + ', both built on the same ' + money(r.our) + ' cost.');
    }
  }

  var bound = new WeakMap();
  var pdpTimer = null;

  function applyHost(host) {
    var obs = bound.get(host);
    if (obs) obs.disconnect();
    try {
      priceNotes(host);
      optionLabels(host);
      costBand(host);
    } finally {
      if (obs) {
        obs.observe(host, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  function applyPdp() {
    document.querySelectorAll(PDP).forEach(function (host) {
      if (!bound.has(host)) {
        bound.set(host, new MutationObserver(function () { schedulePdp(); }));
      }
      applyHost(host);
    });
  }

  function schedulePdp() {
    if (pdpTimer) return;
    pdpTimer = setTimeout(function () { pdpTimer = null; applyPdp(); }, 50);
  }

  function startPdp() {
    applyPdp();
    // The quick-view section element is in the DOM on every page (it ships in
    // the aside section group) but its contents are fetched and swapped in when
    // a card is opened. Watching it is far cheaper than watching document.body,
    // and it is the only way a second host ever appears.
    var qv = document.querySelector('quick-view-section');
    if (qv) new MutationObserver(schedulePdp).observe(qv, { childList: true, subtree: true });
  }

  // ---- The quick view's price follows the variant ------------------------
  // Bricks' `price-component` updates itself on `variant:change` by pulling the
  // new price out of the section HTML the picker just fetched:
  //
  //     updatePrice = (html, sid) => {
  //       let n = html?.querySelector(`price-component[data-section-id="${sid}"]`)
  //       if (n) this.replaceWith(n)
  //     }
  //
  // The quick-view section renders its whole body inside a single <template>
  // (the modal is `temporary: true`, so the panel is built on open). A
  // `<template>`'s contents live in its own DocumentFragment, which
  // `document.querySelector` cannot see into — so that lookup returns null,
  // `replaceWith` never runs, and the price silently keeps whatever variant the
  // modal was opened on. Verified live 2026-08-25 on `maren-polished-steel-set`:
  // picking Ring fired `variant:change`, fetched
  // `?option_values=…&section_id=…__quick-view`, and the response *did* carry a
  // $33.00 price-component — but `html.querySelectorAll('price-component')`
  // returned 0 while `html.querySelector('template').content` returned 1.
  //
  // Stock behaviour, and it predates C208: the modal has always carried a price
  // block. What C208 changed is that the cost band reads that price, so a stale
  // figure now takes the whole ledger with it.
  //
  // NO SECOND REQUEST IS NEEDED. The event detail already carries the resolved
  // variant, prices included, so this writes the two figures straight from it
  // and the observer above recomputes the captions and the cost band from the
  // DOM the way it already does everywhere else. Idempotent: if a theme update
  // ever fixes the lookup, Bricks' own swap lands first and this writes back the
  // same numbers.
  //
  // Scoped to the quick view on purpose — the product page's price component is
  // not inside a template and updates itself correctly. Do not widen it.
  function quickViewPrice(host, variant) {
    var box = host.querySelector('.price__container');
    if (!box) return;
    var sale = box.querySelector('.price__sale-price') ||
      box.querySelector('.price__regular-price');
    if (!sale || typeof variant.price !== 'number') return;

    var sample = sale.textContent;
    var next = moneyLike(sample, variant.price / 100);
    if (sale.textContent.trim() !== next.trim()) sale.textContent = next;

    // A variant with no compare-at must not keep the previous one's — that
    // would print a saving the shopper is not getting. Hidden rather than
    // emptied, so the next variant that does have one can fill it again.
    var compare = box.querySelector('.price__compare-at-price');
    if (!compare) return;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      compare.textContent = moneyLike(sample, variant.compare_at_price / 100);
      compare.style.display = '';
    } else {
      compare.style.display = 'none';
    }
  }

  document.addEventListener('variant:change', function (e) {
    var d = e && e.detail;
    if (!d || !d.variant || !d.sectionId) return;
    var host = document.querySelector('.quick-view__blocks');
    if (!host) return;
    var pc = host.querySelector('price-component');
    if (!pc || pc.dataset.sectionId !== d.sectionId) return;
    // After Bricks' own handler, whether or not it managed the swap.
    setTimeout(function () { quickViewPrice(host, d.variant); }, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPdp);
  } else {
    startPdp();
  }
})();

/* ==========================================================================
   Shipping Protection — cart drawer                      Ledger: C503
   ==========================================================================

   Ported from Tatum Soho (assets/global.js). The markup is
   snippets/sable-protection-row.liquid; the styling is sable-overrides.css §20.

   THE FAILURE MODE THIS GUARDS IS BILLING, NOT UI. `/cart/add.js` INCREMENTS, so
   every duplicated add is a second $2.50 charged to a real customer. Three bugs
   shipped on Tatum before these guards existed; do not simplify them away.

   a · STALE SNAPSHOT. `data-protection-in-cart` is written server-side when the
       drawer renders, and the drawer re-renders on several paths. A handler that
       trusts that attribute can read a stale `false` more than once for a single
       add-to-cart and schedule several adds. Fix: the rendered attribute is a
       cheap pre-filter only — every add re-reads `/cart.js` live first, and an
       `inFlight` lock is held until the add actually lands (polled).

   b · STACKED LISTENERS. Tatum guarded this with `dataset.addonBound`, because its
       toggle node survives a re-render while the setup function runs again.
       DELIBERATE DEVIATION HERE: Bricks replaces the whole drawer subtree on every
       cart change, so the node never survives — a per-node flag would be rebound
       anyway. One delegated listener on `document`, bound once at load, cannot
       stack by construction.

   c · ORPHANED PROTECTION LINE. Remove the last real item and the cart still holds
       a $2.50 protection line, so the drawer never reaches its empty state and a
       shopper can check out with nothing but insurance. Handled in onCartChange.

   d · SABLE-SPECIFIC. Toggling off writes sessionStorage, so the default-on
       setting cannot silently re-add what the shopper just removed. Without it a
       default is a dark pattern.

   Bricks' cart-update signal (the one unknown in the port) is a `cart:change`
   CustomEvent on `document` carrying `{html, sections}`. Dispatching it with a
   fresh `sections` payload is also how we make Bricks re-render — verified in a
   live browser 2026-08-24 by removing a line via raw fetch and watching the
   rendered Total follow. There is no PUB_SUB / subscribe() global here.
   ========================================================================== */
(function () {
  var OFF_KEY = 'protection_toggled_off';
  var inFlight = false;

  /* The DRAWER is not the only place a shopper can remove protection. The /cart
     page renders it as an ordinary line with its own trash link — and it must,
     because there is no toggle there and hiding a charge the shopper cannot see
     or control would be far worse than showing it. But a removal there is not
     our removal, so the default-on rule read the cart, saw protection missing
     with real items present, and put it straight back: remove it on /cart and it
     returned within a second. Verified live 2026-08-24 (`/cart/change` followed
     immediately by `/cart/add.js`). That is the exact dark pattern OFF_KEY exists
     to prevent, arriving through a door the port spec did not think of.

     So: a protection line that DISAPPEARS without us having removed it is read as
     an opt-out, identical to toggling the switch off. `lastKnownHas` starts null
     so a first-ever load infers nothing. */
  var lastKnownHas = null;
  var weRemoved = false;

  /* Add and remove are SERIALIZED, not dropped. An earlier draft guarded both with
     `if (inFlight) return`, which silently discarded whichever the shopper asked for
     second — toggle off during an in-flight add and the switch reads off while the
     cart still holds the $2.50 line. A one-slot queue keeps every request and runs
     them in the order they were made; the live /cart.js read at the head of each
     operation then makes the loser a no-op instead of a second charge. */
  var queue = Promise.resolve();
  function enqueue(fn) {
    queue = queue.then(fn).catch(function () {});
    return queue;
  }

  function drawerEl() { return document.querySelector('cart-drawer'); }
  function rowEl() { return document.querySelector('cart-drawer [data-protection]'); }
  function sectionId() { var d = drawerEl(); return d && d.getAttribute('data-section-id'); }
  function toggledOff() { try { return sessionStorage.getItem(OFF_KEY) === '1'; } catch (e) { return false; } }
  function setToggledOff(v) { try { v ? sessionStorage.setItem(OFF_KEY, '1') : sessionStorage.removeItem(OFF_KEY); } catch (e) {} }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function isProtection(item) { return !!(item.properties && item.properties._protection); }
  function protectionLine(cart) {
    for (var i = 0; i < cart.items.length; i++) if (isProtection(cart.items[i])) return cart.items[i];
    return null;
  }
  function realLineCount(cart) {
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) if (!isProtection(cart.items[i])) n++;
    return n;
  }

  /* Bricks re-renders off this event; see the header note. */
  function rerender(res) {
    var sid = sectionId();
    if (!res || !res.sections || !sid || !res.sections[sid]) return;
    document.dispatchEvent(new CustomEvent('cart:change', {
      detail: { html: res.sections[sid], sections: res.sections }
    }));
  }

  function busy(on) {
    var row = rowEl();
    if (!row) return;
    if (on) row.setAttribute('data-protection-busy', ''); else row.removeAttribute('data-protection-busy');
  }

  /* Guard (a): the live read is the authority, and the lock is held until the
     add is confirmed present in a fresh /cart.js — not merely until the POST
     resolves. Up to 5 polls at 600ms, then give up and release rather than
     wedge the toggle forever. */
  function confirmPresent(want, tries) {
    tries = tries === undefined ? 5 : tries;
    return getCart().then(function (cart) {
      var here = !!protectionLine(cart);
      if (here === want || tries <= 0) return cart;
      return new Promise(function (res) { setTimeout(res, 600); })
        .then(function () { return confirmPresent(want, tries - 1); });
    });
  }

  function addProtection(variantId) {
    if (inFlight || !variantId) return Promise.resolve();
    inFlight = true; busy(true);
    return getCart().then(function (cart) {
      if (protectionLine(cart)) return null;              // already there — stale snapshot
      if (realLineCount(cart) === 0) return null;         // never insure an empty cart
      /* The decision to add is made before this await resolves, so the shopper can
         toggle off in the window between. Re-read the flag at the last possible
         moment or the add lands on top of their opt-out. */
      if (toggledOff()) return null;
      var sid = sectionId();
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _protection: 'protection' } }],
          sections: sid
        })
      }).then(function (r) { return r.json(); });
    }).then(function (res) {
      if (!res) return null;
      return confirmPresent(true).then(function () { rerender(res); });
    }).catch(function () {}).then(function () {
      inFlight = false; busy(false);
    });
  }

  function removeProtection() {
    if (inFlight) return Promise.resolve();
    inFlight = true; busy(true); weRemoved = true;
    return getCart().then(function (cart) {
      var line = protectionLine(cart);
      if (!line) return null;
      var sid = sectionId();
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0, sections: sid })
      }).then(function (r) { return r.json(); });
    }).then(function (res) {
      if (!res) return null;
      return confirmPresent(false).then(function () { rerender(res); });
    }).catch(function () {}).then(function () {
      inFlight = false; busy(false);
    });
  }

  /* Guard (b): one delegated listener, bound once. */
  document.addEventListener('change', function (e) {
    var input = e.target && e.target.closest && e.target.closest('[data-protection-toggle]');
    if (!input) return;
    var row = input.closest('[data-protection]');
    var variantId = row && row.getAttribute('data-protection-variant');
    if (input.checked) {
      setToggledOff(false);
      enqueue(function () { return addProtection(variantId); });
    } else {
      setToggledOff(true);
      enqueue(function () { return removeProtection(); });
    }
  });

  /* The row only exists once Bricks has built the drawer body — the drawer is
     `temporary: true` and renders on open, so on a cold page load with items
     already in the cart there is no `[data-protection]` to read the variant id
     from. Fall back to the product JSON, cached for the page. This is also why
     the id is never hard-coded anywhere in this file. */
  var variantPromise = null;
  function protectionVariantId() {
    var row = rowEl();
    if (row) return Promise.resolve(row.getAttribute('data-protection-variant'));
    if (!variantPromise) {
      variantPromise = fetch('/products/shipping-protection.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { return p && p.variants && p.variants[0] ? String(p.variants[0].id) : null; })
        .catch(function () { return null; });
    }
    return variantPromise;
  }

  /* Guard (c) + the default-on. Runs after every Bricks re-render and once at
     load. Both branches are no-ops once settled, so this cannot loop: the add
     requires realLineCount > 0 and no protection line, and the cleanup requires
     realLineCount === 0 and a protection line. */
  function reconcile() {
    if (inFlight) return;
    getCart().then(function (cart) {
      var has = !!protectionLine(cart);
      var real = realLineCount(cart);

      /* Removed somewhere that is not our toggle — treat it as an opt-out. */
      if (lastKnownHas === true && !has && !weRemoved && real > 0) {
        setToggledOff(true);
      }
      weRemoved = false;
      lastKnownHas = has;

      if (has && real === 0) {                                    // (c) orphan
        enqueue(function () { return removeProtection(); });
        return;
      }
      if (!has && real > 0 && !toggledOff()) {                    // default-on, unless (d)
        return protectionVariantId().then(function (id) {
          if (id) enqueue(function () { return addProtection(id); });
        });
      }
    }).catch(function () {});
  }

  document.addEventListener('cart:change', function () { setTimeout(reconcile, 0); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();

